<template>
	<Card>
		<div class="info group">
			<h3>Multi-round campaign settings</h3>
			<p>
				This campaign is currently on
				<strong>round {{ round }}</strong
				>, which started on <strong>{{ roundStart }}</strong
				>.
			</p>
			<p>
				Progressing a campaign to a new round will reset all vote tallies and
				only allow voting on the submissions in the shortlist below.
			</p>
			<Note
				>Moving to a new round is <strong>permanent</strong> and cannot be
				undone. Old votes and submissions will not be deleted and will still be
				visible in downloaded campaign data.</Note
			>
		</div>
		<div class="controls group">
			<ProgressCampaignForm
				ref="form"
				v-show="showControls"
				:campaign="campaign"
				:submissions="submissions"
				:loading="progressing"
				@submit="onProgress"
			/>
			<div v-if="!showControls" class="controls__info">
				<h3>Not enough submissions in shortlist</h3>
				<p>
					Add at least <strong>two submissions</strong> to the shortlist using
					the
					<InlineButtonIcon bg="white"><IconPromote /></InlineButtonIcon>
					buttons in the table above. Only <strong>approved</strong> submissions
					can be added.
				</p>
			</div>
		</div>
		<h3>Shortlist</h3>
		<StrapiList
			ref="list"
			source="/submissions"
			:query="query"
			:watch-url="false"
			@update="onUpdate"
			@refresh="onRefresh"
		>
			<template v-slot="list">
				<SubmissionsTable
					v-bind="list"
					:campaign="campaign"
					:sortable="false"
					:pagination="false"
					is-shortlist
				/>
			</template>
		</StrapiList>
	</Card>
</template>

<script>
import { enums } from 'protoswipe-shared'
import IconPromote from 'mdi-vue/PlaylistPlus'
import Card from '@/components/shared/Card'
import Note from '@/components/admin/Note'
import StrapiList from '@/components/admin/StrapiList'
import SubmissionsTable from '@/components/admin/SubmissionsTable'
import InlineButtonIcon from '@/components/admin/InlineButtonIcon'
import ProgressCampaignForm from '@/components/admin/ProgressCampaignForm'

import api from '@/mixins/api'
import toast from '@/mixins/toast'

export default {
	name: 'Shortlist',
	props: {
		campaign: Object
	},
	data() {
		return {
			count: 0,
			results: [],
			columns: [
				{ label: 'Title' },
				{ label: 'Name' },
				{ label: 'Email' },
				{ label: 'Submitted' },
				{ label: 'Actions' }
			],
			progressing: false
		}
	},
	computed: {
		round() {
			return this.campaign.round + 1
		},
		nextRound() {
			return this.round + 1
		},
		roundStart() {
			return new Date(this.campaign.roundCutoff).toLocaleDateString()
		},
		query() {
			return {
				campaign: this.campaign.id,
				round: this.campaign.round,
				status: enums.SubmissionStatus.approved,
				promote: true,
				_limit: -1
			}
		},
		showControls() {
			return this.count >= 2
		},
		submissions() {
			return this.results.map(item => ({
				title: item.title,
				name: item.name,
				date: new Date(item.created_at).toLocaleDateString()
			}))
		}
	},
	methods: {
		refresh() {
			if (this.$refs.list) this.$refs.list.refresh()
		},
		onRefresh(options) {
			this.$emit('refresh', options)
		},
		onUpdate({ results, count }) {
			this.results = results
			this.count = count
		},
		async onProgress(body) {
			this.progressing = true
			try {
				const endpoint = `/campaigns/${this.campaign.id}/progress`
				const res = await this.post(endpoint, { body })
				if (!res.ok) {
					this.toastErr(`Failed to move campaign to round ${this.nextRound}.`)
				} else {
					const { round } = await res.json()
					this.$emit('progress', { round })
					this.toast(`Successfully moved campaign to round ${this.nextRound}.`)

					// Close form popup
					if (this.$refs.form) this.$refs.form.hideModal()
				}
			} catch (err) {
				this.toastErr(`Failed to move campaign to round ${this.nextRound}.`)
			}
			this.progressing = false
		}
	},
	mixins: [api, toast],
	components: {
		Card,
		Note,
		StrapiList,
		SubmissionsTable,
		InlineButtonIcon,
		ProgressCampaignForm,
		IconPromote
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/vars';
@import '~scss/layout';
@import '~scss/components';

.info {
	@include truncate-margin;
}

.group {
	margin-bottom: 2rem;
}

.controls {
	padding: 1.5rem;
	background-color: $blue-98;
	color: $black;

	&__info > * {
		margin: 0;
		&:not(:last-child) {
			margin-bottom: 0.5rem;
		}
	}
}
</style>
